import React, { useState } from 'react';
import { Download, Trash2, Search, Filter, SortAsc, SortDesc } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { driveService } from '../../services/driveService';
import { DriveFile, DriveFolder, DRIVE_FOLDERS, getFileIcon, formatFileSize } from '../../types/drive';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface FileListProps {
  files: DriveFile[];
  loading: boolean;
  onFileDelete?: () => void;
  className?: string;
}

export const FileList: React.FC<FileListProps> = ({ 
  files, 
  loading, 
  onFileDelete, 
  className = '' 
}) => {
  const { user } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<DriveFolder | 'all'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'date' | 'size'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);

  // Filtra file
  const filteredFiles = files.filter(file => {
    // Filtro per cartella
    if (selectedFolder !== 'all' && file.folder !== selectedFolder) {
      return false;
    }

    // Filtro per ricerca
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const fileNameLower = file.originalName.toLowerCase();
      return fileNameLower.includes(searchLower);
    }

    return true;
  });

  // Ordina file
  const sortedFiles = [...filteredFiles].sort((a, b) => {
    const order = sortOrder === 'desc' ? -1 : 1;

    switch (sortBy) {
      case 'name':
        return a.originalName.localeCompare(b.originalName) * order;
      case 'date':
        return (a.uploadedAt.seconds - b.uploadedAt.seconds) * order;
      case 'size':
        return (a.fileSize - b.fileSize) * order;
      default:
        return 0;
    }
  });

  // Download file
  const handleDownload = async (file: DriveFile) => {
    try {
      if (file.downloadURL) {
        // Crea link temporaneo per download
        const link = document.createElement('a');
        link.href = file.downloadURL;
        link.download = file.originalName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Fallback: ottieni URL download
        const downloadURL = await driveService.getDownloadURL(file.storagePath);
        const link = document.createElement('a');
        link.href = downloadURL;
        link.download = file.originalName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Errore download file:', error);
      alert('Errore nel download del file');
    }
  };

  // Elimina file
  const handleDelete = async (file: DriveFile) => {
    if (!user?.uid) return;

    const isConfirmed = window.confirm(
      `Sei sicuro di eliminare il file "${file.originalName}"? Questa azione non può essere annullata.`
    );

    if (!isConfirmed) return;

    try {
      setDeletingFileId(file.id);
      await driveService.deleteFile(file.id, user.uid);
      
      if (onFileDelete) {
        onFileDelete();
      }
    } catch (error) {
      console.error('Errore eliminazione file:', error);
      alert('Errore nell\'eliminazione del file');
    } finally {
      setDeletingFileId(null);
    }
  };

  // Verifica permessi per eliminazione
  const canDeleteFile = (file: DriveFile): boolean => {
    if (!user?.uid) return false;
    return user.uid === file.userId;
  };

  if (loading) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="animate-pulse space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Filtri e ricerca */}
      <div className="space-y-4">
        {/* Barra ricerca */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca file..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filtri e ordinamento */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Filtro cartella */}
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={selectedFolder}
              onChange={(e) => setSelectedFolder(e.target.value as DriveFolder | 'all')}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="all">Tutte le cartelle</option>
              {DRIVE_FOLDERS.map(folder => (
                <option key={folder.value} value={folder.value}>
                  {folder.icon} {folder.label}
                </option>
              ))}
            </select>
          </div>

          {/* Ordinamento */}
          <div className="flex items-center space-x-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'date' | 'size')}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
            >
              <option value="date">Data</option>
              <option value="name">Nome</option>
              <option value="size">Dimensione</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Lista file */}
      {sortedFiles.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">📁</div>
          <p className="text-gray-500 dark:text-gray-400">
            {searchTerm || selectedFolder !== 'all' 
              ? 'Nessun file trovato con i filtri selezionati'
              : 'Nessun file caricato'
            }
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedFiles.map(file => (
            <div
              key={file.id}
              className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
            >
              {/* Informazioni file */}
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className="text-2xl">{getFileIcon(file.mimeType)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {file.originalName}
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                    <span>{formatFileSize(file.fileSize)}</span>
                    <span>•</span>
                    <span>{format(file.uploadedAt.toDate(), 'dd/MM/yyyy HH:mm', { locale: it })}</span>
                    <span>•</span>
                    <span className="flex items-center">
                      {DRIVE_FOLDERS.find(f => f.value === file.folder)?.icon} 
                      {DRIVE_FOLDERS.find(f => f.value === file.folder)?.label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Azioni */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleDownload(file)}
                  className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  title="Scarica file"
                >
                  <Download className="h-4 w-4" />
                </button>

                {canDeleteFile(file) && (
                  <button
                    onClick={() => handleDelete(file)}
                    disabled={deletingFileId === file.id}
                    className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                    title="Elimina file"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Statistiche */}
      {sortedFiles.length > 0 && (
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          {sortedFiles.length} file{files.length !== sortedFiles.length && ` (${files.length} totali)`}
        </div>
      )}
    </div>
  );
}; 